import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { conversationsForUserState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import { getConversationLabel, isConversationReadOnly, type UserId } from '@rock/types';
import type { FC } from 'react';

export const UserChatsTab: FC<{ userId: UserId }> = ({ userId }) => {
    const [conversations, conversationsLoadable] = useLoadableAtomValue(conversationsForUserState(userId), []);
    const onRowClick = useRowClick();

    return (
        <Card>
            <CardHeader>
                <CardTitle>Chat conversations</CardTitle>
                <CardDescription>
                    Newest activity first. Costs are cumulative per conversation; conversations become read-only in the
                    app after 30 days.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {conversations.length === 0 ? (
                    <div className="section-description">
                        {conversationsLoadable.state === 'loading'
                            ? 'Loading conversations…'
                            : conversationsLoadable.state === 'hasError'
                              ? 'Couldn’t load conversations.'
                              : 'No conversations yet.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Conversation</TableHead>
                                <TableHead className="text-right">Messages</TableHead>
                                <TableHead className="text-right">Tokens in/out</TableHead>
                                <TableHead className="text-right">Est. cost</TableHead>
                                <TableHead className="text-right">Updated</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {conversations.map((conversation) => (
                                <TableRow
                                    key={conversation.id}
                                    className="cursor-pointer transition-colors hover:bg-muted/50"
                                    onClick={onRowClick(
                                        `/directory/users/${userId}/chats/${encodeURIComponent(conversation.id)}`,
                                    )}
                                >
                                    <TableCell className="max-w-[320px] font-medium">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate">{getConversationLabel(conversation)}</span>
                                            {isConversationReadOnly(conversation) && (
                                                <Badge variant="outline">Read-only</Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">{conversation.messages.length}</TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {conversation.tokensIn.toLocaleString()} /{' '}
                                        {conversation.tokensOut.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        ${conversation.estimatedCostUsd.toFixed(3)}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(conversation.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
